import BankAccountFormatDropdown from "components/dropdowns/reactselect/BankAccountFormatDropdown";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import React from "react";
import { getBankAccountFormat } from "utilities";
import { routes } from "utilities/routing/generatedRoutes";
import AbaRoutingNumberInput from "./edit/AbaRoutingNumberInput";
import BsbCodeInput from "./edit/BsbCodeInput";
import IbanInput from "./edit/IbanInput";
import SortCodeInput from "./edit/SortCodeInput";
import SwiftCodeInput from "./edit/SwiftCodeInput";
import { BankAccountNewInfo, getValueIfVisibleOrDefault } from "./models";
import TransitNumberInput from "./edit/TransitNumberInput";
import InstitutionNumberInput from "./edit/InstitutionNumberInput";
import { BookkeepingMultiModalPicker } from "components/Picker/Bookkeeping/BookkeepingMultiModalPicker";

export const BankAccountNewSection = () => {
  const [value, setValue] = React.useState<BankAccountNewInfo>({
    title: "",
    beneficiaryName: "",
    format: getBankAccountFormat("genericIban"),
    abaRoutingNumber: "",
    accountNumber: "",
    bsbCode: "",
    iban: "",
    swiftCode: "",
    sortCode: "",
    bankAddress: "",
    branchName: "",
    branchCode: "",
    institutionNumber: "",
    transitNumber: "",
    memo: "",
    bookkeepings: []
  });

  const onChange = (newData: Partial<BankAccountNewInfo>) => {
    setValue({ ...value, ...newData });
  };

  const submit = () => {
    const instruction: server.dto.CreateBankAccountInstruction = {
      format: value.format.value,
      title: value.title,
      beneficiaryName: value.beneficiaryName,
      accountNumber: getValueIfVisibleOrDefault(value.accountNumber, value.format.displayAccountNumberInput),
      ibanNumber: getValueIfVisibleOrDefault(value.iban, value.format.displayIbanNumberInput),
      bankAddress: value.bankAddress,
      branchName: value.branchName,
      branchCode: value.branchCode,
      swiftCode: getValueIfVisibleOrDefault(value.swiftCode, value.format.displaySwiftCodeInput),
      sortCode: getValueIfVisibleOrDefault(value.sortCode, value.format.displaySortCodeInput),
      bsbCode: getValueIfVisibleOrDefault(value.bsbCode, value.format.displayBsbCodeInput),
      abaRoutingNumber: getValueIfVisibleOrDefault(value.abaRoutingNumber, value.format.displayAbaRoutingNumberInput),
      institutionNumber: getValueIfVisibleOrDefault(value.institutionNumber, value.format.displayInstitutionNumberInput),
      transitNumber: getValueIfVisibleOrDefault(value.transitNumber, value.format.displayTransitNumberInput),
      memo: value.memo,
      bookkeepingIds: value.bookkeepings.map(bk => bk.bookkeepingId),
    };
    return window.CoreApi.BankAccount.CreateBankAccount(instruction);
  }

  return <EditSectionTemplate
    submit={submit}
    redirectUrl={(itemId) => routes.bankAccount_Detail(itemId).absolute}
    sections={[{
      key: "bankAccountDetails",
      type: "regular",
      supportArticleId: serverReference.supportArticleIds.whatIsABankAccount,
      title: RESX.BankAccount.resxBankAccount,
      fields: [{
        key: "title",
        label: RESX.GeneralLabels.InternalTitle,
        renderEditNew: () => <RequiredTextInput
          validatorKey="title"
          maxLength={serverReference.constants.bankAccount.maxTitleLength}
          value={value.title}
          onChange={(title) => { onChange({ title }); }}
        />,
      }, {
        key: "format",
        label: RESX.BankAccount.Format,
        renderEditNew: () => <BankAccountFormatDropdown
          value={value.format}
          onChange={(format) => onChange({ format })}
        />
      }, {
        key: "beneficiaryName",
        label: RESX.BankAccount.BeneficiaryName,
        renderEditNew: () => <RequiredTextInput
          validatorKey="beneficiaryName"
          maxLength={serverReference.constants.bankAccount.maxBeneficiaryNameLength}
          value={value.beneficiaryName}
          onChange={(beneficiaryName) => { onChange({ beneficiaryName }); }}
        />,
      }, {
        key: "routingNumber",
        label: RESX.BankAccount.RoutingNumber,
        hidden: !value.format.displayAbaRoutingNumberInput,
        renderEditNew: () => <AbaRoutingNumberInput
          validatorKey="abaRoutingNumber"
          value={value.abaRoutingNumber}
          onChanged={(abaRoutingNumber) => { onChange({ abaRoutingNumber }); }}
        />
      }, {
        key: "institutionNumber",
        label: RESX.BankAccount.InstitutionNumber,
        hidden: !value.format.displayInstitutionNumberInput,
        renderEditNew: () => <InstitutionNumberInput
          validatorKey="institutionNumber"
          value={value.institutionNumber}
          onChanged={(institutionNumber) => { onChange({ institutionNumber }); }}
        />
      }, {
        key: "accountNumber",
        label: RESX.BankAccount.AccountNumber,
        hidden: !value.format.displayAccountNumberInput,
        renderEditNew: () => <OptionalTextInput
          maxLength={serverReference.constants.bankAccount.maxAccountNumberLength}
          value={value.accountNumber}
          onChange={(accountNumber) => { onChange({ accountNumber }); }}
        />,
      }, {
        key: "transitNumber",
        label: RESX.BankAccount.TransitNumber,
        hidden: !value.format.displayTransitNumberInput,
        renderEditNew: () => <TransitNumberInput
          validatorKey="transitNumber"
          value={value.transitNumber}
          onChanged={(transitNumber) => { onChange({ transitNumber }); }}
        />
      }, {
        key: "bsbCode",
        label: RESX.BankAccount.BsbCode,
        hidden: !value.format.displayBsbCodeInput,
        renderEditNew: () => <BsbCodeInput
          value={value.bsbCode}
          onChanged={bsbCode => onChange({ bsbCode })}
          validatorKey="bsbCode"
        />
      }, {
        key: "ibanNumber",
        label: RESX.BankAccount.IbanNumber,
        hidden: !value.format.displayIbanNumberInput,
        renderEditNew: () => <IbanInput
          value={value.iban}
          onChanged={(iban) => onChange({ iban })}
          validatorKey="iban"
        />
      }, {
        key: "sortCode",
        label: RESX.BankAccount.SortCode,
        hidden: !value.format.displaySortCodeInput,
        renderEditNew: () => <SortCodeInput
          value={value.sortCode}
          onChanged={(sortCode) => onChange({ sortCode })}
          validatorKey="iban"
        />
      }, {
        key: "swiftCode",
        label: RESX.BankAccount.SwiftCode,
        hidden: !value.format.displaySwiftCodeInput,
        renderEditNew: () => <SwiftCodeInput
          value={value.swiftCode}
          onChanged={(swiftCode) => { onChange({ swiftCode }); }}
          validatorKey="swiftCode"
        />
      }, {
        key: "bankAddress",
        label: RESX.BankAccount.Bank,
        renderEditNew: () => <OptionalTextInput
          maxLength={serverReference.constants.bankAccount.maxBankAddressLength}
          placeholder={RESX.BankAccount.BankPlaceholder}
          value={value.bankAddress}
          onChange={(bankAddress) => { onChange({ bankAddress }); }}
        />
      }, {
        key: "branchName",
        label: RESX.BankAccount.BranchName,
        hidden: !value.format.displayBranchNameInput,
        renderEditNew: () => <OptionalTextInput
          maxLength={serverReference.constants.bankAccount.maxBranchNameLength}
          value={value.branchName}
          onChange={(branchName) => { onChange({ branchName }); }}
        />
      }, {
        key: "branchCode",
        label: RESX.BankAccount.BranchCode,
        hidden: !value.format.displayBranchCodeInput,
        renderEditNew: () => <OptionalTextInput
          maxLength={serverReference.constants.bankAccount.maxBranchCodeLength}
          value={value.branchCode}
          onChange={(branchCode) => { onChange({ branchCode }); }}
        />
      }, {
        key: "memo",
        label: RESX.BankAccount.Memo,
        renderEditNew: () => <OptionalTextInput
          maxLength={serverReference.constants.bankAccount.maxMemoLength}
          value={value.memo}
          onChange={(memo) => { onChange({ memo }); }}
        />
      }, {
        key: "bookkeepings",
        label: RESX.Bookkeeping.Bookkeepings,
        renderEditNew: () => <div className="main-section-template-fields-padding">
          <BookkeepingMultiModalPicker
            value={value.bookkeepings}
            onChange={bookkeepings => onChange({ bookkeepings })}
            canCreateNew={false}
            bookkeepingSecurityScope="limitByUserAccess"
            required
            validatorKey="bookkeepings"
          />
        </div>,
      }]
    }]}
  />
}